<template>
  <Screen>
    <AppBar :showBackButton="false" />
    <div class="content">
      <h3 class="mt-0 mt-lg-4">
        <span class="text-dim">Hello</span>
        {{ user.name }}
      </h3>
      <div class="overview-container mt-4">
        <component
          class="box"
          v-for="box in overviewBoxes"
          :key="box.id"
          :class="box.color"
          :href="box.redirect"
          :is="box.redirect ? 'inertia-link' : 'div'"
        >
          <div class="icon">
            <component :is="box.icon" weight="duotone" />
          </div>
          <div class="title">
            <h1 v-html="box.value"></h1>
            <h3 class="mb-0 text-dim" v-text="box.title"></h3>
          </div>
          <div class="arrow-icon" v-if="box.redirect">
            <i class="fas fa-chevron-right"></i>
          </div>
        </component>
      </div>
      <div class="row">
        <div class="col-lg-6">
          <div class="card card-body mt-4">
            <div
              class="
                card-title
                d-flex
                justify-content-between
                align-items-center
              "
            >
              <h4 class="mb-0">
                <b class="text-warning">Open</b>
                <span class="text-dim"> support tickets</span>
              </h4>
              <inertia-link
                :href="$route('admin.support.index')"
                class="btn btn-primary btn-sm"
              >
                View all<i class="fa fa-chevron-right ml-1"></i>
              </inertia-link>
            </div>
            <div class="table-responsive" v-if="supportTickets.data.length">
              <table class="table">
                <thead class="thead-light">
                  <tr>
                    <th>From</th>
                    <th>Message</th>
                    <th>Time</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="item in supportTickets.data" :key="item.id">
                    <th>
                      <span class="d-block">
                        {{ item.user.name }}
                        <inertia-link
                          :href="
                            $route('admin.users.details', {
                              id: item.user.id,
                            })
                          "
                          class="btn btn-rounded-sm btn-primary-neutral ml-1"
                          v-tooltip="'View user'"
                        >
                          <i class="fa fa-eye"></i>
                        </inertia-link>
                      </span>
                      <a
                        class="sub-title"
                        :href="`tel:${item.user.mobile}`"
                        v-text="item.user.mobile"
                        v-tooltip="'Click to call'"
                      ></a>
                      <small>&bull;</small>
                      <a
                        class="sub-title"
                        :href="`mailto:${item.user.email}`"
                        v-text="item.user.email"
                        v-tooltip="'Click to email'"
                      ></a>
                    </th>
                    <td>
                      <div style="max-width: 480px">
                        <VueReadMoreSmooth :lines="2">
                          <span>{{ item.message }}</span>
                        </VueReadMoreSmooth>
                      </div>
                    </td>
                    <td>{{ item.time_ago }}</td>
                    <td>
                      <inertia-link
                        :href="$route('admin.support.view', { id: item.id })"
                        class="btn btn-light btn-sm"
                      >
                        View
                      </inertia-link>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <NoData title="No open support tickets" v-else>
              <span></span>
            </NoData>
          </div>
        </div>
        <div class="col-lg-6">
          <div class="card card-body mt-4">
            <div
              class="
                card-title
                d-flex
                justify-content-between
                align-items-center
              "
            >
              <h4 class="mb-0">
                <span class="text-dim">New contact inquiries</span>
              </h4>
              <inertia-link
                :href="$route('admin.contactus')"
                class="btn btn-primary btn-sm"
              >
                View all<i class="fa fa-chevron-right ml-1"></i>
              </inertia-link>
            </div>
            <div class="table-responsive" v-if="contactUs.data.length">
              <table class="table">
                <thead class="thead-light">
                  <tr>
                    <th>From</th>
                    <th>Subject</th>
                    <th>Time</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="item in contactUs.data" :key="item.id">
                    <th>
                      <span class="d-block">{{ item.name }}</span>
                      <a
                        class="sub-title"
                        :href="`tel:${item.mobile}`"
                        v-text="item.mobile"
                        v-tooltip="'Click to call'"
                      ></a>
                      <small>&bull;</small>
                      <a
                        class="sub-title"
                        :href="`mailto:${item.email}`"
                        v-text="item.email"
                        v-tooltip="'Click to email'"
                      ></a>
                    </th>
                    <th>
                      {{ item.subject || "-" }}
                    </th>
                    <td>{{ item.time_ago }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <NoData title="No new inquiries" v-else>
              <span></span>
            </NoData>
          </div>
        </div>
      </div>
    </div>
  </Screen>
</template>
<script>
import {
  PhCalendarCheck,
  PhCalendar,
  PhUsers,
  PhPaperPlaneRight,
  PhHeadset,
  PhCalculator,
  PhTree,
  PhFlowerLotus,
  PhCheckCircle,
} from "phosphor-vue";

import VueReadMoreSmooth from "vue-read-more-smooth";

export default {
  title: "Dashboard",
  components: {
    VueReadMoreSmooth,
    PhCalendarCheck,
    PhCalendar,
    PhUsers,
    PhPaperPlaneRight,
    PhHeadset,
    PhCalculator,
    PhTree,
    PhFlowerLotus,
    PhCheckCircle,
  },
  props: {
    contactUs: {
      type: [Array, Object],
      default: () => ({}),
    },
    supportTickets: {
      type: [Array, Object],
      default: () => ({}),
    },
  },
  data() {
    const thisMonth = () => {
      // get month name
      const month = new Date().getMonth();
      const months = [
        "January",
        "February",
        "March",
        "April",
        "May",
        "June",
        "July",
        "August",
        "September",
        "October",
        "November",
        "December",
      ];
      return months[month];
    };

    return {
      overviewBoxes: [
        {
          title: "Users who accessed the app today",
          id: "users-accessed-today",
          value: 0,
          icon: PhUsers,
          color: "primary",
          redirect: this.$route("admin.users.index", { last_used: "today" }),
        },
        {
          title: "Total active users",
          id: "total-active-users",
          value: 0,
          icon: PhCheckCircle,
          color: "success",
          redirect: this.$route("admin.users.index", { status: 1 }),
        },
        {
          title: "Users signups in " + thisMonth(),
          id: "total-users-month",
          value: 0,
          icon: PhCalendarCheck,
          color: "info",
          redirect: this.$route("admin.users.index"),
        },
        {
          title: "Revenue generated in " + thisMonth(),
          id: "revenue-this-month",
          value: "₹0",
          icon: PhCalculator,
          color: "danger",
          redirect: null,
        },
        {
          title: "Open support tickets",
          id: "open-support-tickets",
          value: 0,
          icon: PhHeadset,
          color: "danger",
          redirect: this.$route("admin.support.index"),
        },
        {
          title: "New contact inquiries",
          id: "new-contact-inquiries",
          value: 0,
          icon: PhPaperPlaneRight,
          color: "danger",
          redirect: this.$route("admin.contactus"),
        },
        {
          title: "Workouts completed by users in " + thisMonth(),
          id: "workouts-completed-this-month",
          value: 0,
          icon: PhFlowerLotus,
          color: "warning",
          redirect: null,
        },
        {
          title: "Total trees planted",
          id: "total-trees-planted",
          value: '0  <small class="text-dim font-weight-bold">trees</small>',
          icon: PhTree,
          color: "success",
          redirect: null,
        },
      ],
    };
  },
  mounted() {
    setTimeout(() => {
      this.getCounts();
    });
  },
  methods: {
    getCounts() {
      axios.get(this.$route("admin.index.dataCounts")).then(({ data }) => {
        this.overviewBoxes.forEach((box) => {
          box.value = data[box.id] || box.value;
        });
      });
    },
  },
  computed: {
    user() {
      return this.$page.props.user;
    },
  },
};
</script>
<style lang="scss" scoped>
.overview-container {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  grid-gap: 1rem;

  @media (max-width: 576px) {
    grid-template-columns: 1fr;
    grid-gap: 0.8rem;
  }
}
.box {
  // vars
  --icon-color: var(--primary-dark);
  --icon-background: rgba(var(--primary-rgb), 0.1);
  --icon-background-hover: var(--primary-contrast);

  --box-color-hover: var(--primary-contrast);
  --box-background-hover: var(--primary);

  display: flex;
  align-items: center;
  gap: 0.8rem;
  padding: 1.3rem 1rem;
  border-radius: 14px;
  background-color: var(--bg-translucent);
  box-shadow: var(--box-shadow);
  color: var(--dark);
  transition: var(--transition);

  &a {
    cursor: pointer;
  }

  .icon {
    font-size: 2.1rem;
    height: 3.4rem;
    width: 3.6rem;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 12px;
    background: var(--icon-background);
    color: var(--icon-color);
    transition: var(--transition);
  }
  .title {
    flex: 1;
    h1 {
      font-size: 2rem;
      font-weight: 600;
      margin-bottom: 0rem;
    }
    h3 {
      font-size: 0.9rem;
      font-weight: 600;
      font-family: var(--font-family);
    }
  }
  .arrow-icon {
    font-size: 1.1rem;
    color: var(--lighter);
  }
  &:hover {
    background-color: var(--box-background-hover);
    color: var(--box-color-hover);
    .icon {
      background: var(--icon-background-hover);
    }
    .arrow-icon {
      color: var(--icon-background-hover);
    }
  }

  &.success {
    --icon-color: var(--success-dark);
    --icon-background: rgba(var(--success-rgb), 0.2);
    --icon-background-hover: var(--success-contrast);

    --box-color-hover: var(--success-contrast);
    --box-background-hover: var(--success);
  }
  &.warning {
    --icon-color: var(--warning-dark);
    --icon-background: rgba(var(--warning-rgb), 0.2);
    --icon-background-hover: var(--warning-contrast);

    --box-color-hover: var(--warning-contrast);
    --box-background-hover: var(--warning);
  }
  &.info {
    --icon-color: var(--info-dark);
    --icon-background: rgba(var(--info-rgb), 0.2);
    --icon-background-hover: var(--info-contrast);

    --box-color-hover: var(--info-contrast);
    --box-background-hover: var(--info);
  }
  &.danger {
    --icon-color: var(--danger-dark);
    --icon-background: rgba(var(--danger-rgb), 0.2);
    --icon-background-hover: var(--danger-contrast);

    --box-color-hover: var(--danger-contrast);
    --box-background-hover: var(--danger);
  }

  &.full-span {
    grid-column: span 2;
  }

  @media (max-width: 576px) {
    &.full-span {
      grid-column: span 1;
    }
    border-radius: 12px;
    padding: 1rem 0.8rem;
    .icon {
      font-size: 1.8rem;
      height: 3.2rem;
      width: 3.4rem;
    }
    .title {
      h1 {
        font-size: 1.5rem;
      }
      h3 {
        font-size: 0.8rem;
      }
    }
    .arrow-icon {
      font-size: 0.9rem;
    }
  }
}
</style>